/**
 * VoiceRide Socket.IO client singleton + React hooks
 * ===================================================
 * Connects to the backend Socket.IO server for:
 *   - Real-time matching (driver:online, guest:search, match:found)
 *   - Notifications (new_notification)
 *   - Location updates (location:update)
 *
 * Usage:
 *   import { getSocket, useMatchingSocket } from '../hooks/useSocket';
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { BACKEND_URL, SOCKET_IO_PATH } from '../utils/apiConfig';
import { getAudioAlertsEnabled } from './useMatchChime';

// Helper: play ascending chime (C5 → E5 → G5)
function playMatchChime() {
  if (!getAudioAlertsEnabled()) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [523.25, 659.25, 783.99];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.4);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.12);
      osc.stop(ctx.currentTime + i * 0.12 + 0.45);
    });
    setTimeout(() => ctx.close(), 1500);
  } catch {}
}

// ── Singleton Socket Instance ──

let socketInstance = null;
let socketDisabled = true; // DISABLED — socket connections removed for stability

export function getSocket() {
  if (socketDisabled) return null;
  return socketInstance;
}

// ── Connection Hook ──

export function useSocketConnection() {
  const [connected, setConnected] = useState(false);
  const socket = getSocket();

  useEffect(() => {
    if (!socket) return;
    if (!socket.connected) socket.connect();

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    if (socket.connected) setConnected(true);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, [socket]);

  return { socket, connected };
}

// ── Matching Hook (Driver) ──

export function useDriverMatching() {
  const { socket, connected } = useSocketConnection();
  const [matchRequests, setMatchRequests] = useState([]);
  const [isOnline, setIsOnline] = useState(false);

  useEffect(() => {
    if (!socket) return;
    const onAck = () => setIsOnline(true);
    const onMatchRequest = (data) => {
      playMatchChime();
      setMatchRequests((prev) => [...prev, data]);
    };

    socket.on('driver:ack', onAck);
    socket.on('match:request', onMatchRequest);

    return () => {
      socket.off('driver:ack', onAck);
      socket.off('match:request', onMatchRequest);
    };
  }, [socket]);

  const goOnline = useCallback((driverData) => {
    if (socket) socket.emit('driver:online', driverData);
  }, [socket]);

  const acceptMatch = useCallback((matchData) => {
    if (socket) socket.emit('match:accept', matchData);
  }, [socket]);

  const rejectMatch = useCallback((matchData) => {
    if (socket) socket.emit('match:reject', matchData);
  }, [socket]);

  return { connected, isOnline, matchRequests, goOnline, acceptMatch, rejectMatch };
}

// ── Matching Hook (Guest / Passenger) ──

export function useGuestMatching() {
  const { socket, connected } = useSocketConnection();
  const [matches, setMatches] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    if (!socket) return;
    const onAck = () => setIsSearching(true);
    const onMatchFound = (data) => {
      playMatchChime();
      setMatches(data.matches || []);
    };
    const onConfirmed = (data) => {
      setConfirmed(data);
    };
    const onRejected = () => {
      // Could show a toast; for now just log
    };

    socket.on('guest:ack', onAck);
    socket.on('match:found', onMatchFound);
    socket.on('match:confirmed', onConfirmed);
    socket.on('match:rejected', onRejected);

    return () => {
      socket.off('guest:ack', onAck);
      socket.off('match:found', onMatchFound);
      socket.off('match:confirmed', onConfirmed);
      socket.off('match:rejected', onRejected);
    };
  }, [socket]);

  const search = useCallback((guestData) => {
    setMatches([]);
    setConfirmed(null);
    if (socket) socket.emit('guest:search', guestData);
  }, [socket]);

  return { connected, isSearching, matches, confirmed, search };
}

// ── Location Update ──

export function useLiveLocation(intervalMs = 5000) {
  const { socket, connected } = useSocketConnection();
  const watchRef = useRef(null);

  const startTracking = useCallback(() => {
    if (!navigator.geolocation || !socket) return;
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        if (socket) socket.emit('location:update', {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      null,
      { enableHighAccuracy: true, maximumAge: intervalMs }
    );
  }, [socket, intervalMs]);

  const stopTracking = useCallback(() => {
    if (watchRef.current !== null) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
  }, []);

  useEffect(() => () => stopTracking(), [stopTracking]);

  return { connected, startTracking, stopTracking };
}

// ── Notifications Hook ──

export function useNotifications(userId) {
  const { socket, connected } = useSocketConnection();
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!userId || !connected || !socket) return;

    socket.emit('notifications:subscribe', { user_id: userId });

    const onNew = (notification) => {
      setNotifications((prev) => [notification, ...prev]);
    };

    socket.on('new_notification', onNew);
    return () => socket.off('new_notification', onNew);
  }, [socket, connected, userId]);

  return { connected, notifications };
}

export default getSocket;
